import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleFullContent, articles } from '@/mocks/articles';

function getArticleJsonLd(
  article: { title: string; date: string; sections: { heading: string; content: string }[] },
  slug: string,
) {
  const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL) || 'https://pandabuy.forum';
  const articleMeta = articles.find((a) => a.slug === slug);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'PandaBuy Spreadsheet Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'PandaBuy Spreadsheet Hub',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
      },
    },
    image: articleMeta ? articleMeta.imageUrl : '',
    url: siteUrl + '/article/' + slug,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl + '/article/' + slug,
    },
    description: article.sections[0]?.content.substring(0, 160) || '',
    articleBody: article.sections.map((s) => s.heading + '\n' + s.content).join('\n\n'),
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleFullContent[slug as keyof typeof articleFullContent] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center pt-20 px-4">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">Article Not Found</h1>
          <p className="mt-4 text-foreground-500">The article you are looking for does not exist.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getArticleJsonLd(article, slug || '')) }}
      />
      <Navbar />
      <article className="pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="w-full px-4 md:px-6 lg:px-12 max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">Guides</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium truncate">{article.title}</span>
          </nav>

          <header className="mb-10">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-4">
              <time dateTime={article.date} className="text-xs text-foreground-400">
                Last updated: {new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </time>
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">Guide</span>
            </div>
          </header>

          <div className="prose-custom space-y-10">
            {article.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                  {section.content.split('\n\n').map((paragraph, pIdx) => (
                    <p key={pIdx}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-14 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/50">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
              Ready to Explore More Spreadsheets?
            </h3>
            <p className="text-sm text-foreground-500 mb-5">
              Now that you understand how pandabuyspreadsheet collections work, head over to our main hub to browse the latest curated lists across all categories.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Spreadsheet Hub
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-foreground-300 text-foreground-700 font-semibold text-sm hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}